import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { collectionApis } from '../../../apis/collectionApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage.jsx';
import Loading from '../../../components/elements/loading/Loading';
import Text from '../../../components/elements/text/Text.jsx';
import ConfigContent from '../../../components/page-sections/file-config/ConfigContent.jsx';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel.jsx';
import { useSolrInstance } from '../../../contexts/SolrContext';
import { PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import { isSolrCloud, sentenceCase } from '../../../utils/utils';

const SolrConfigPage = () => {
  const { projectId, instanceId } = useParams();
  const { instance } = useSolrInstance();
  const { t } = useTranslation();

  const [collections, setCollections] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const getCollections = async () => {
    setLoading(true);
    setError(false);
    try {
      const solrCloud = isSolrCloud(instance.type);

      if (solrCloud) {
        const { data: collectionRes } =
          await collectionApis.getSolrCloudCollections(projectId, instanceId);
        const collectionList = collectionRes.collections;
        setCollections(collectionList);
      } else {
        const { data: collectionRes } = await collectionApis.getCollections(
          projectId,
          instanceId
        );
        const collectionList = Object.keys(collectionRes.status);
        setCollections(collectionList);
      }
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!instance) return;
    getCollections();
  }, []);

  const collectionsPage = buildPath({
    path: PRIVATE_ROUTE.INSTANCE_COLLECTIONS,
    params: { projectId, instanceId },
  });

  if (loading) return <Loading />;

  return (
    instance && (
      <Container>
        <Breadcrumbs instanceName={instance?.name} />
        <TitlePanel title={t('config', { ns: 'titles' })} visible />
        {error ? (
          <AlertMessage
            action={getCollections}
            severity="error"
            message={sentenceCase(
              t('fetch', {
                ns: 'errors',
                content: t('collection', { ns: 'instance' }),
              })
            )}
          />
        ) : collections.length > 0 ? (
          <ConfigContent
            collections={collections}
            instanceType={instance.type}
          />
        ) : (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Text align="center">
              {t('configNoCollection', { ns: 'descriptions' })}
            </Text>
            <Text align="center">
              {t('configNoCollection2', { ns: 'descriptions' })}
            </Text>
            <Box sx={{ mt: 2 }}>
              <Button
                component={RouterLink}
                to={collectionsPage}
                variant="outlined"
              >
                {t('moveToCollection', { ns: 'buttons' })}
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    )
  );
};

export default SolrConfigPage;
